import {
  Divider,
  List,
  ListItemIcon,
  ListItemSecondaryAction,
  ListItemText,
  Avatar as MuiAvatar,
  IconButton as MuiIconButton,
  ListItem as MuiListItem,
  Paper as MuiPaper,
  Popover,
  styled,
  Switch,
  Typography,
} from '@mui/material';
import * as Ramda from 'ramda';
import { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link } from 'react-router-dom';

// icons
import ExitToAppIcon from '@mui/icons-material/ExitToApp';

import { useFeature } from '@growthbook/growthbook-react';
import { StaffPhotoAvatar } from '../../../../../components/user-avatar/StaffPhotoAvatar';
import {
  useActor,
  type UseActorReturn,
} from '../../../../../components/hooks/useActor';
import { useAllow } from '../../../../../components/hooks/useAllow';
import { useAuth } from '../../../../../components/hooks/useAuth';
import { usePersona } from '../../../../../components/hooks/usePersona';
import Showable from '../../../../../components/Showable';
import { FEATURE_FLAG_KEYS } from '../../../../../config/growthbook';
import { REDACTED_FIELD } from '../../../../../config/logrocket';
import { languages } from '../../../../../i18n/i18n.language-config';
import { useEnvQuery } from '../../../../../queries/useEnvQuery';
import { toggleTheme } from '../../../../../reducers/UiSettingsReducers';
import {
  toggleFeatureFlags,
  toggleRenewalSubmissionConfidenceUi,
  toggleUKEnvFlag,
  toggleAUEnvFlag,
} from '../../../../../store/actions';
import {
  GROWTHBOOK_FEATURE_SETTINGS,
  THEME_TYPES,
} from '../../../../../store/consts/ui-settings.store.consts';
import { selectGrowthbook } from '../../../../../store/selectors/ui-settings.selectors';
import type { RootStore } from '../../../../../types';
import { ENVIRONMENTS } from '../../../../../types/common/env.dto';
import { usePlatformLanguage } from '../../../../../utils/hooks/usePlatformLanguage';
import { withShowable } from '../../../../_global/lib/withShowable';
import { DynamicFormsToggleListItem } from './AvatarMenuListItem/DynamicFormsToggleListItem';
import { SubmissionsViewToggleListItem } from './AvatarMenuListItem/SubmissionsViewToggleListItem';

const IconButton = withShowable(MuiIconButton);
const ListItem = withShowable(MuiListItem);

// My Profile is always available from the avatar menu for the Cowbell persona;
// every other persona keeps its prior visibility (Pool: own-profile permission,
// everyone else: only while ghosting). Extracted to a helper to avoid a nested
// ternary in JSX.
function getMyProfileVisibility(
  persona: ReturnType<typeof usePersona>,
  canManageOwnProfile: boolean,
  isGhosting: boolean
): boolean {
  if (persona.isCowbell) {
    return true;
  }
  if (persona.isPool) {
    return canManageOwnProfile;
  }
  return isGhosting;
}

interface HeaderAvatarProps {
  readonly actor: UseActorReturn;
}

/**
 * The signed-in user's avatar. In the Cowbell persona, UserAvatar, which keeps the photo
 * getProfilePhoto() fetched (actor.avatarUrl) until the staff list is live; elsewhere, as before.
 */
export function HeaderAvatar({ actor }: HeaderAvatarProps) {
  return (
    <StaffPhotoAvatar
      email={actor.email}
      name={actor.fullName}
      size="sm"
      label={actor.fullName || actor.email}
      legacySrc={actor.avatarUrl}
    >
      <Avatar src={actor.avatarUrl || undefined}>
        <Typography variant="caption">
          {actor.initials ||
            `${actor.firstName?.[0] || ''}${actor.lastName?.[0] || ''}`}
        </Typography>
      </Avatar>
    </StaffPhotoAvatar>
  );
}

export const UserAvatarMenu = () => {
  const auth = useAuth();
  const actor = useActor();
  const persona = usePersona();
  const canManageOwnProfile = useAllow('user_profile:manage');
  const env = useEnvQuery();
  const dispatch = useDispatch();
  const theme = useSelector((state: RootStore) => state.uiSettings.theme);
  const {
    featureFlagsEnabled,
    ukEnvironmentEnabled,
    auEnvironmentEnabled,
    new_agent_experience,
    renewal_submission_confidence_ui,
  } = useSelector(selectGrowthbook);
  const region = usePlatformLanguage();

  const AGENCY_DASHBOARD_PREREQ = useFeature(
    FEATURE_FLAG_KEYS.AGENCY_DASHBOARD_PREREQ
  );

  const [anchor, setAnchor] = useState(null);

  function handleThemeChange() {
    dispatch(toggleTheme());
  }

  function handlePopoverOpen(event: any) {
    setAnchor(event.currentTarget);
  }

  function handlePopoverClose(event: any) {
    setAnchor(null);
  }

  function handleGrowthBookSwitch() {
    dispatch(toggleFeatureFlags());
  }

  function handleUKToggle() {
    dispatch(toggleUKEnvFlag());
  }

  function handleAUSToggle() {
    dispatch(toggleAUEnvFlag());
  }

  function handleAgencyDashboardGrowthbookSwitch() {
    dispatch(
      toggleFeatureFlags(GROWTHBOOK_FEATURE_SETTINGS.NEW_AGENT_EXPERIENCE)
    );
  }

  const open = Boolean(anchor);
  const cowbellerInsideAgency = actor.isCowbell && persona.isAgency;
  const agentInsideAgency = actor.isAgency && persona.isAgency;

  const isDevEnv = [
    ENVIRONMENTS.DEV_ENV_ID,
    ENVIRONMENTS.DEV_SPACE,
    ENVIRONMENTS.LOCAL,
  ].some(Ramda.equals(env.data?.envName));
  const isQaEnv = [ENVIRONMENTS.QA_ENV_ID].some(
    Ramda.equals(env.data?.envName)
  );
  const isStagingEnv = [ENVIRONMENTS.STAGING_ENV_ID].some(
    Ramda.equals(env.data?.envName)
  );

  return (
    <>
      <IconButton size="small" onClick={handlePopoverOpen}>
        <HeaderAvatar actor={actor} />
      </IconButton>
      <Popover
        id="mouse-over-popover"
        open={open}
        anchorEl={anchor}
        onClose={handlePopoverClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
      >
        <Paper style={{ marginTop: 8 }}>
          <List dense>
            <ListItem>
              <ListItemText
                primary={
                  <div
                    className={`tertia-text contrast-text ${REDACTED_FIELD}`}
                  >
                    {actor.fullName}
                  </div>
                }
                secondary={actor.roleName}
                disableTypography
              />
            </ListItem>
            <ListItem
              show={getMyProfileVisibility(
                persona,
                canManageOwnProfile,
                auth.isGhosting
              )}
              button
              component={Link}
              to={`${persona.basePath}/manage/me`}
            >
              <ListItemText
                primary={<span className="contrast-text">My profile</span>}
                disableTypography
              />
            </ListItem>
            <ListItem
              show={auth.isGhosting && persona.isAgency}
              button
              component={Link}
              to={`${persona.basePath}/me/licenses`}
            >
              <ListItemText
                primary={<span className="contrast-text">My licenses</span>}
                disableTypography
              />
            </ListItem>
            <ListItem>
              <ListItemText
                primary={
                  <span
                    className="contrast-text"
                    style={{ textTransform: 'capitalize' }}
                  >
                    {theme}
                  </span>
                }
                disableTypography
              />
              <ListItemSecondaryAction
                onClick={() => handleThemeChange()}
                style={{ right: 0 }}
              >
                <Switch value={theme} checked={theme === THEME_TYPES.LIGHT} />
              </ListItemSecondaryAction>
            </ListItem>
            <Showable show={actor.isCowbell || isDevEnv || isQaEnv}>
              <ListItem>
                <ListItemText
                  primary={
                    <span
                      className="contrast-text"
                      style={{ textTransform: 'capitalize' }}
                    >
                      Feature Flags
                    </span>
                  }
                  disableTypography
                />
                <ListItemSecondaryAction
                  onClick={handleGrowthBookSwitch}
                  style={{ right: 0 }}
                >
                  <Switch
                    value={featureFlagsEnabled}
                    checked={featureFlagsEnabled}
                  />
                </ListItemSecondaryAction>
              </ListItem>
            </Showable>

            <Showable show={isDevEnv || isQaEnv}>
              <ListItem>
                <ListItemText
                  primary={
                    <span
                      className="contrast-text"
                      style={{ textTransform: 'capitalize' }}
                    >
                      Simulate UK env.
                    </span>
                  }
                  disableTypography
                />
                <ListItemSecondaryAction
                  onClick={handleUKToggle}
                  style={{ right: 0 }}
                >
                  <Switch
                    value={ukEnvironmentEnabled}
                    checked={ukEnvironmentEnabled}
                    disabled={auEnvironmentEnabled}
                  />
                </ListItemSecondaryAction>
              </ListItem>
            </Showable>

            <Showable show={isDevEnv || isQaEnv}>
              <ListItem>
                <ListItemText
                  primary={
                    <span
                      className="contrast-text"
                      style={{ textTransform: 'capitalize' }}
                    >
                      Simulate AU env.
                    </span>
                  }
                  disableTypography
                />
                <ListItemSecondaryAction
                  onClick={handleAUSToggle}
                  style={{ right: 0 }}
                >
                  <Switch
                    value={auEnvironmentEnabled}
                    checked={auEnvironmentEnabled}
                    disabled={ukEnvironmentEnabled}
                  />
                </ListItemSecondaryAction>
              </ListItem>
            </Showable>

            <Showable show={isDevEnv || isQaEnv || isStagingEnv}>
              <DynamicFormsToggleListItem />
              <SubmissionsViewToggleListItem />
            </Showable>

            <Showable
              show={
                AGENCY_DASHBOARD_PREREQ.on &&
                region === languages['en-US'] &&
                (cowbellerInsideAgency || agentInsideAgency)
              }
            >
              <ListItem>
                <ListItemText
                  primary={
                    <span
                      className="contrast-text"
                      style={{ textTransform: 'capitalize' }}
                    >
                      Latest Dashboard
                    </span>
                  }
                  disableTypography
                />
                <ListItemSecondaryAction
                  onClick={handleAgencyDashboardGrowthbookSwitch}
                  style={{ right: 0 }}
                >
                  <Switch
                    value={new_agent_experience}
                    checked={new_agent_experience}
                  />
                </ListItemSecondaryAction>
              </ListItem>
            </Showable>
            <Showable show={actor.isCowbell}>
              <ListItem>
                <ListItemText
                  primary={
                    <span className="contrast-text">
                      Renewal Submission Confidence
                    </span>
                  }
                  disableTypography
                />
                <ListItemSecondaryAction
                  onClick={() =>
                    dispatch(toggleRenewalSubmissionConfidenceUi())
                  }
                  style={{ right: 0 }}
                >
                  <Switch
                    value={renewal_submission_confidence_ui}
                    checked={renewal_submission_confidence_ui}
                  />
                </ListItemSecondaryAction>
              </ListItem>
            </Showable>
            <Divider />
            <ListItem button component={Link} to="/logout">
              <ListItemText
                primary="Logout"
                disableTypography
                className="contrast-text"
              />
              <ListItemIcon
                style={{
                  position: 'absolute',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  right: 0,
                  lineHeight: 0,
                }}
              >
                <ExitToAppIcon />
              </ListItemIcon>
            </ListItem>
          </List>
        </Paper>
      </Popover>
    </>
  );
};

const Avatar = styled(MuiAvatar)(({ theme }: any) => ({
  color: theme.palette.common.white,
  background: theme.palette.common.cowbellBlue,
  width: '2rem',
  height: '2rem',
}));

const Paper = styled(MuiPaper)(({ theme }: any) => ({
  minWidth: 250,
  background: `${theme.palette.background.modal} !important`,
}));
